/**
 * IntegrationBalancePanel — compact balance + burn-rate + runway readout
 * for a single Integration card on the /integrations page.
 *
 * Behavior:
 *   - On mount, fetches GET /integrations/{id}/balance (cheap; reads
 *     the stored balance + computes 7-day burn-rate from the response
 *     cache).
 *   - For providers where the backend supports auto-refresh (DeepSeek
 *     today), shows a Refresh button that hits ?refresh=true to pull
 *     a live value from the provider's balance API.
 *   - For everyone else, the balance is operator-entered via a small
 *     prompt() — same compact UI, no extra modal.
 *
 * Designed to render inline inside the existing provider card without
 * dominating the layout. Total height ≈ 60-90px depending on whether
 * any balance is set yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Coins, Loader2, RefreshCw, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import integrationService, {
  BalanceResponse,
} from '../services/integrationService';

interface Props {
  integrationId: number;
  providerDisplayName: string;
}

const fmtMoney = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `$${v.toFixed(2)}`;

const fmtRunway = (days: number | null | undefined): string => {
  if (days === null || days === undefined) return '—';
  if (days >= 365) return `${(days / 365).toFixed(1)} yr`;
  if (days >= 30) return `${(days / 30).toFixed(1)} mo`;
  return `${days.toFixed(0)} d`;
};


const IntegrationBalancePanel: React.FC<Props> = ({
  integrationId, providerDisplayName,
}) => {
  const [data, setData] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    const res = await integrationService.getIntegrationBalance(
      integrationId, { refresh },
    );
    setLoading(false);
    setRefreshing(false);
    if (res.error) {
      // Silent on initial load — most providers won't have a balance
      // wired up yet. Surface explicitly on refresh.
      if (refresh) toast.error(res.error);
      return;
    }
    if (res.data) setData(res.data);
  }, [integrationId]);

  useEffect(() => { load(false); }, [load]);

  const handleManualSet = async () => {
    const current = data?.balance_usd ?? null;
    const entered = window.prompt(
      `Set remaining balance for ${providerDisplayName} (USD).\n` +
        `Leave blank to clear.\n\n` +
        `Tip: regular API keys for Anthropic / OpenAI / Gemini / Grok don't ` +
        `expose balance, so this is operator-entered. DeepSeek auto-pulls; ` +
        `prefer Refresh on that one.`,
      current !== null ? String(current) : '',
    );
    if (entered === null) return;  // Cancel
    const trimmed = entered.trim();
    let next: number | null = null;
    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (Number.isNaN(parsed) || parsed < 0) {
        toast.error('Enter a non-negative dollar amount, or leave blank to clear.');
        return;
      }
      next = parsed;
    }
    const res = await integrationService.setIntegrationBalance(integrationId, next);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) setData(res.data);
    toast.success('Balance updated');
  };

  if (loading) {
    return (
      <div className="mt-2 text-xs text-gray-400 flex items-center">
        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
        Loading balance…
      </div>
    );
  }
  if (!data) return null;

  const {
    balance_usd, balance_updated_at, balance_source,
    auto_refresh_supported, usage,
  } = data;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 text-xs">
      <div className="flex items-center justify-between mb-1.5">
        <span className="inline-flex items-center text-gray-500 font-medium uppercase tracking-wide text-[10px]">
          <Coins className="w-3 h-3 mr-1" />
          Balance &amp; burn
        </span>
        <div className="flex items-center gap-1">
          {auto_refresh_supported && (
            <button
              type="button"
              onClick={() => load(true)}
              disabled={refreshing}
              className="inline-flex items-center text-gray-500 hover:text-blue-600 disabled:opacity-50"
              title="Refresh from provider API"
            >
              {refreshing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleManualSet}
            className="inline-flex items-center text-gray-500 hover:text-blue-600"
            title="Set balance manually"
          >
            <Pencil className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-gray-800">
        <div>
          <div className="text-[10px] text-gray-500 uppercase">Balance</div>
          <div className="font-semibold">{fmtMoney(balance_usd)}</div>
          {balance_source && (
            <div className="text-[10px] text-gray-400">{balance_source}</div>
          )}
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">Avg/day (7d)</div>
          <div className="font-semibold">{fmtMoney(usage.avg_usd_per_day)}</div>
          <div className="text-[10px] text-gray-400">
            {usage.input_tokens.toLocaleString()} in · {usage.output_tokens.toLocaleString()} out
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">Runway</div>
          <div className="font-semibold">{fmtRunway(usage.runway_days)}</div>
          {balance_usd === null && (
            <div className="text-[10px] text-amber-600">Set balance</div>
          )}
        </div>
      </div>
      {balance_updated_at && (
        <div className="text-[10px] text-gray-400 mt-1.5">
          Balance updated {new Date(balance_updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default IntegrationBalancePanel;
